import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import AdminRoute from './components/AdminRoute.jsx';
import Layout from './components/Layout.jsx';
import Loader from './components/Loader.jsx';
import PrivateRoute from './components/PrivateRoute.jsx';
import Bracket from './pages/Bracket.jsx';
import { AdminQueueProvider } from './context/AdminQueueContext.jsx';
import Access from './pages/Access.jsx';
import AccessRequests from './pages/Admin/AccessRequests.jsx';
import AuditLog from './pages/Admin/AuditLog.jsx';
import CommandDeck from './pages/Admin/CommandDeck.jsx';
import DesignRequests from './pages/Admin/DesignRequests.jsx';
import Grants from './pages/Admin/Grants.jsx';
import PackageArt from './pages/Admin/PackageArt.jsx';
import RegistrationSettings from './pages/Admin/RegistrationSettings.jsx';
import UserCreate from './pages/Admin/UserCreate.jsx';
import UserEdit from './pages/Admin/UserEdit.jsx';
import UsersList from './pages/Admin/UsersList.jsx';
import Dashboard from './pages/Dashboard.jsx';
import EventSets from './pages/EventSets.jsx';
import Login from './pages/Login.jsx';
import BillingReturn from './pages/BillingReturn.jsx';
import MyRequests from './pages/MyRequests.jsx';
import PackageProduction from './pages/PackageProduction.jsx';
import Deck from './pages/Deck.jsx';
import Setup from './pages/Setup.jsx';

// Public marketing landing page. Lazy-loaded so its CSS + fonts only download
// when someone actually visits /welcome, never on the authed app pages.
const Landing = lazy(() => import('./pages/Landing.jsx'));

function Chrome({ children }) {
  return <Layout>{children}</Layout>;
}

// Admin pages bring their own chrome (the Signal Rail in AdminShell), so they
// render through `Layout bare`: keeps the sticky navbar, drops Layout's centred
// .container and footer. The container would otherwise cap the content column
// near 1320px, which the fixed rail plus a wide table can't live inside;
// AdminShell recreates the footer in the content column.
// AdminQueueProvider sits above AdminShell so both the rail's badges and the
// Command Deck's portals read one set of pending counts.
function AdminChrome({ children }) {
  return (
    <AdminRoute>
      <Layout bare>
        <AdminQueueProvider>{children}</AdminQueueProvider>
      </Layout>
    </AdminRoute>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Full-page immersive login — no navbar chrome. */}
      <Route path="/login" element={<Login />} />
      {/* Public marketing landing — full-bleed with its own footer, wrapped in
          bare chrome so the sticky app navbar stays on top (always a way back).
          Static segment so it wins over the /:slug catch-all below. */}
      <Route
        path="/welcome"
        element={
          <Layout bare>
            <Suspense fallback={<Loader label="Loading…" />}>
              <Landing />
            </Suspense>
          </Layout>
        }
      />
      {/* Public, but uses the layout. */}
      <Route path="/setup" element={<Chrome><Setup /></Chrome>} />

      {/* Signed-in users — / is the package picker. */}
      <Route path="/" element={<PrivateRoute><Chrome><Dashboard /></Chrome></PrivateRoute>} />
      <Route path="/sets" element={<PrivateRoute><Chrome><EventSets /></Chrome></PrivateRoute>} />
      <Route path="/access/:packageSlug" element={<PrivateRoute><Chrome><Access /></Chrome></PrivateRoute>} />
      <Route path="/my-requests" element={<PrivateRoute><Chrome><MyRequests /></Chrome></PrivateRoute>} />
      {/* UPayments sends clients back here (static route — must precede the
          /:slug catch-all). */}
      <Route path="/billing/return" element={<PrivateRoute><Chrome><BillingReturn /></Chrome></PrivateRoute>} />

      {/* Admin-only. Static segments so they win over the /:slug catch-all
          below — including bare /admin, which would otherwise be swallowed by
          it and render PackageProduction's "no access" screen. */}
      <Route path="/admin" element={<AdminChrome><CommandDeck /></AdminChrome>} />
      <Route path="/admin/users" element={<AdminChrome><UsersList /></AdminChrome>} />
      <Route path="/admin/users/new" element={<AdminChrome><UserCreate /></AdminChrome>} />
      <Route path="/admin/users/:id" element={<AdminChrome><UserEdit /></AdminChrome>} />
      <Route path="/admin/access-requests" element={<AdminChrome><AccessRequests /></AdminChrome>} />
      <Route path="/admin/design-requests" element={<AdminChrome><DesignRequests /></AdminChrome>} />
      <Route path="/admin/grants" element={<AdminChrome><Grants /></AdminChrome>} />
      <Route path="/admin/package-art" element={<AdminChrome><PackageArt /></AdminChrome>} />
      <Route path="/admin/registration" element={<AdminChrome><RegistrationSettings /></AdminChrome>} />
      <Route path="/admin/audit-log" element={<AdminChrome><AuditLog /></AdminChrome>} />

      {/* Catch-all for /<package-slug>. Static segments above (e.g. /sets,
          /admin/users, /login) match first thanks to React Router's
          specificity rules; this only catches single-segment paths that
          aren't already handled. PackageProduction renders a "no access"
          screen if the slug is unknown or the user has no grants. */}
      {/* The OBS deck alone, full screen with no chrome (src/pages/Deck.jsx):
          an iPad or a phone as a stream deck. */}
      <Route path="/:slug/deck" element={<PrivateRoute><Deck /></PrivateRoute>} />
      <Route path="/:slug/bracket" element={<PrivateRoute><Chrome><Bracket /></Chrome></PrivateRoute>} />
      <Route path="/:slug" element={<PrivateRoute><Chrome><PackageProduction /></Chrome></PrivateRoute>} />
    </Routes>
  );
}
